import type { Editor } from "./canvas";
import type { DraftingShape } from "../shared/schema";
import { openingPlacement, nearestWallSegment } from "../shared/drafting";
/** Openings belong to walls, so transforms and deletion follow their parent. */
export function installDraftingBehavior(editor: Editor) {
  let updating = false;
  const replaying = () =>
    (editor as Editor & { isReplayingHistory(): boolean }).isReplayingHistory();
  const before = editor.sideEffects.registerBeforeChangeHandler(
    "shape",
    (prev, next, source) => {
      if (
        updating ||
        replaying() ||
        source !== "user" ||
        prev.type !== "drafting" ||
        next.type !== "drafting" ||
        !["door", "window"].includes(next.props.kind)
      )
        return next;
      const wall = editor.getShape(prev.parentId);
      if (wall?.type !== "drafting" || wall.props.kind !== "wall") return next;
      const moved = next.x !== prev.x || next.y !== prev.y;
      const hit = moved
        ? nearestWallSegment(wall.props.points, { x: next.x, y: next.y })
        : undefined;
      const segment = hit?.segment ?? next.props.segment,
        position = hit?.t ?? next.props.position;
      const p = openingPlacement(
        wall.props.points,
        segment,
        position,
        next.props.width,
      );
      return {
        ...next,
        parentId: wall.id,
        x: p.x,
        y: p.y,
        rotation: p.rotation,
        props: {
          ...next.props,
          width: p.width,
          segment,
          position,
          thickness: wall.props.thickness,
        },
      };
    },
  );
  const after = editor.sideEffects.registerAfterChangeHandler(
    "shape",
    (prev, next, source) => {
      if (
        updating ||
        replaying() ||
        source !== "user" ||
        next.type !== "drafting" ||
        next.props.kind !== "wall" ||
        prev.type !== "drafting" ||
        JSON.stringify(prev.props) === JSON.stringify(next.props)
      )
        return;
      updating = true;
      try {
        // The wall determines these transforms even when an opening is locked.
        // Scope the override to derived child updates; their locks remain intact.
        editor.run(
          () =>
            editor.updateShapes(
              editor
                .getSortedChildIdsForParent(next.id)
                .map((id) => editor.getShape(id))
                .filter(
                  (s): s is DraftingShape =>
                    s?.type === "drafting" &&
                    ["door", "window"].includes(s.props.kind),
                )
                .map((s) => {
                  const p = openingPlacement(
                    next.props.points,
                    s.props.segment,
                    s.props.position,
                    s.props.width,
                  );
                  return {
                    id: s.id,
                    type: "drafting" as const,
                    x: p.x,
                    y: p.y,
                    rotation: p.rotation,
                    props: { width: p.width, thickness: next.props.thickness },
                  };
                }),
            ),
          { ignoreShapeLock: true },
        );
      } finally {
        updating = false;
      }
    },
  );
  return () => {
    before();
    after();
  };
}
